import { useState, useRef } from 'react';
import tiktokApi from '../../api/tiktokApi';
import Header from '../Header';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch, faSearch, faXmarkCircle } from '@fortawesome/free-solid-svg-icons';
import ErrorPopup from '../Modal/popup/ErrorPopup';
import SuccessPopup from '../Modal/popup/SuccessPopup';
import Modal from '../Modal';
import handleErrorAsync from '../../utils/handleErrorAsync';
import shopeApi from '../../api/shopeeApi';
import lazadaApi from '../../api/lazadaApi';

function FindOrder({ logo, title, description, merchant }) {
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('Thêm đơn thành công');
    const [input, setInput] = useState('');
    const [isLoading, setisLoading] = useState(false);
    const [modalType, setModalType] = useState(2);
    const [openModal, setOpenModal] = useState(false);
    const inputRef = useRef(null);

    // Get merchant-specific colors
    const getMerchantColors = () => {
        switch (merchant) {
            case 'lazada':
                return {
                    titleColor: 'text-blue-800',
                    borderColor: 'border-blue-600',
                    buttonGradient: 'from-blue-600 to-blue-800',
                    buttonHover: 'hover:from-blue-700 hover:to-blue-900',
                    inputFocus: 'focus:border-blue-600 focus:ring-blue-600',
                    logoSize: 'h-24',
                };
            case 'tiktok':
                return {
                    titleColor: 'text-rose-800',
                    borderColor: 'border-rose-600',
                    buttonGradient: 'from-rose-600 to-rose-800',
                    buttonHover: 'hover:from-rose-700 hover:to-rose-900',
                    inputFocus: 'focus:border-rose-600 focus:ring-rose-600',
                    logoSize: 'h-24',
                };
            case 'shopee':
                return {
                    titleColor: 'text-primary-600',
                    borderColor: 'border-primary-600',
                    buttonGradient: 'from-primary-600 to-primary-800',
                    buttonHover: 'hover:from-primary-700 hover:to-primary-900',
                    inputFocus: 'focus:border-primary-600 focus:ring-primary-600',
                    logoSize: 'h-24',
                };
            default:
                return {
                    titleColor: 'text-primary-600',
                    borderColor: 'border-primary-600',
                    buttonGradient: 'from-primary-600 to-primary-800',
                    buttonHover: 'hover:from-primary-700 hover:to-primary-900',
                    inputFocus: 'focus:border-primary-600 focus:ring-primary-600',
                    logoSize: 'h-24',
                };
        }
    };

    const colors = getMerchantColors();

    const onFindOrder = async () => {
        const orderId = (input || '').toString().trim();
        if (!orderId) return;
        setisLoading(true);
        setModalType(1);
        try {
            let res;
            switch (merchant) {
                case 'tiktok':
                    res = await tiktokApi.findOrder(orderId);
                    break;
                case 'shopee':
                    res = await shopeApi.findOrder(orderId);
                    break;
                case 'lazada':
                    res = await lazadaApi.findOrder(orderId);
                    break;
                default:
                    break;
            }
            setSuccessMsg(res?.data?.message || 'Thêm đơn thành công');
            setModalType(2);
        } catch (error) {
            console.log(error);
            setModalType(3);
            handleErrorAsync(error, setErrorMsg);
        }
        setisLoading(false);
        setOpenModal(true);
    };

    const handleClearInput = () => {
        setInput('');
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.focus();
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isLoading && input.trim()) {
            onFindOrder();
        }
    };

    return (
        <div className="h-[100dvh] flex flex-col justify-between bg-gradient-to-b from-gray-50 via-white to-gray-50">
            <Header />
            <div className="h-full flex flex-col justify-center items-center p-4 md:p-6 gap-6 md:gap-8 max-w-md mx-auto w-full">
                {/* Logo Section */}
                {logo && (
                    <div className={`${colors.logoSize} w-full flex items-center justify-center mb-4`}>
                        <img
                            className="object-contain h-full"
                            src={logo.url}
                            alt={logo.alt}
                        />
                    </div>
                )}

                {/* Title and Description Section */}
                <div className="w-full text-center space-y-3">
                    <h1 className={`font-bold text-xl md:text-2xl ${colors.titleColor} leading-tight`}>
                        {title || 'Nhập đơn hàng của bạn'}
                    </h1>
                    <div className="text-sm md:text-base text-content-2 leading-relaxed px-2 max-w-lg mx-auto">
                        {description || 'Áp dụng cho thành viên Longhousee.com'}
                    </div>
                </div>

                {/* Search Input Section */}
                <div className="w-full space-y-4">
                    <div className="relative flex gap-2">
                        <div className="relative flex-1">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-content-3">
                                <FontAwesomeIcon icon={faSearch} />
                            </div>
                            <input
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                type="text"
                                className={`w-full pl-12 pr-12 py-4 rounded-xl border-2 ${colors.borderColor} bg-surface shadow-lg ${colors.inputFocus} focus:outline-none focus:ring-2 transition-all duration-200 placeholder:text-content-3 placeholder:font-medium`}
                                placeholder="Nhập mã đơn hàng"
                            />
                            {input && (
                                <button
                                    onClick={handleClearInput}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-content-3 hover:text-content-2 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faXmarkCircle} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Search Button */}
                    <button
                        disabled={isLoading || !input.trim()}
                        onClick={onFindOrder}
                        className={`w-full py-4 rounded-xl bg-gradient-to-r ${colors.buttonGradient} ${colors.buttonHover} text-white font-bold text-lg shadow-lg transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none active:scale-95 flex items-center justify-center gap-2`}
                    >
                        {isLoading ? (
                            <>
                                <FontAwesomeIcon
                                    icon={faCircleNotch}
                                    className="animate-spin"
                                />
                                <span>Đang tìm...</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSearch} />
                                <span>Tìm đơn</span>
                            </>
                        )}
                    </button>
                </div>

                {/* Info Box */}
                {(() => {
                    const getInfoBoxConfig = () => {
                        switch (merchant) {
                            case 'lazada':
                                return {
                                    bgColor: 'bg-blue-50',
                                    borderColor: 'border-blue-200',
                                    titleColor: 'text-blue-800',
                                    icon: '💡',
                                    message: 'Nhập chính xác mã đơn hàng để tìm kiếm nhanh chóng và chính xác nhất.'
                                };
                            case 'tiktok':
                                return {
                                    bgColor: 'bg-rose-50',
                                    borderColor: 'border-rose-200',
                                    titleColor: 'text-rose-800',
                                    icon: '💡',
                                    message: 'Nhập chính xác mã đơn hàng để tìm kiếm nhanh chóng và chính xác nhất.'
                                };
                            case 'shopee':
                                return {
                                    bgColor: 'bg-orange-50',
                                    borderColor: 'border-orange-200',
                                    titleColor: 'text-orange-800',
                                    icon: '💡',
                                    message: 'Nhập chính xác mã đơn hàng để tìm kiếm nhanh chóng và chính xác nhất.'
                                };
                            default:
                                return {
                                    bgColor: 'bg-surface-sunken',
                                    borderColor: 'border-line',
                                    titleColor: 'text-content',
                                    icon: '💡',
                                    message: 'Nhập chính xác mã đơn hàng để tìm kiếm nhanh chóng và chính xác nhất.'
                                };
                        }
                    };
                    const infoConfig = getInfoBoxConfig();
                    return (
                        <div className={`w-full mt-4 p-2 ${infoConfig.bgColor} border ${infoConfig.borderColor} rounded-xl shadow-sm`}>
                            <div className="flex items-start gap-3">
                                <div className={`text-xl flex-shrink-0`}>{infoConfig.icon}</div>
                                <div className="text-sm text-content-2 leading-relaxed flex-1">
                                    <p className={`font-semibold ${infoConfig.titleColor} mb-1.5`}>Lưu ý:</p>
                                    <p className="text-content-2">{infoConfig.message}</p>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>

            <Modal isOpen={openModal}>
                {modalType === 3 && (
                    <ErrorPopup setModal={setOpenModal} errorMsg={errorMsg} />
                )}
                {modalType === 2 && (
                    <SuccessPopup
                        setModal={setOpenModal}
                        successMsg={successMsg}
                    />
                )}
            </Modal>
        </div>
    );
}

export default FindOrder;
